<template>
    <div class="welcome-map-page">
        <div class="map-icons">
            <a title="Login" @click.prevent="goLogin">
                <i class="fa fa-sign-in"></i>
            </a>
            <a title="Viabilidade" @click.prevent="goViability">
                <i class="fa fa-map-marker"></i>
            </a>
        </div>

        <div id="welcome-map" class="map">
            <div class="map-layer-switch" @mouseover="blockTooltip">
                <div ref="layerSwitcherTarget" class="layerSwitcher"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'
import { onMounted, onUnmounted, ref } from 'vue'
import { Modal } from 'ant-design-vue'
import Map from 'ol/Map'
import View from 'ol/View'
import { defaults as defaultControlsOL } from 'ol/control'
import { defaults as defaultInteractions } from 'ol/interaction'
import { fromLonLat } from 'ol/proj'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import CircleStyle from 'ol/style/Circle'
import { layerSwitcher, scaleLine, fullScreenBtn } from '../../../../open-layers/default-controls'
import { getMapDefaultCenterView } from '../../../../open-layers/map-defaults'
import { buildWelcomeMapLayers, fetchWelcomeMapConfig } from '../../../../open-layers/welcome-map-layers'
import { checkAreaOfInterest, getDeviceLocation } from '../../../../open-layers/helpers'
import {baseLayers} from "../../../../open-layers/baseLayers";

const mapRef = ref(null)
const layerSwitcherTarget = ref(null)
const areaOfInterest = ref({ success: true })
const locationGranted = ref(false)

let userLocationLayer = null
let watchId = null

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }

function blockTooltip() {
    const tooltip = document.getElementById('tooltip')
    if (tooltip) tooltip.style.display = 'none'
}

function requireGeolocation(message) {
    if (!locationGranted.value) {
        Modal.warning({
            title: 'Geolocalização',
            content: 'Permita o acesso à sua localização no navegador para continuar. O navegador deve exibir um pedido de permissão ao carregar o mapa.',
        })
        return false
    }
    if (window.checkLocation && !areaOfInterest.value.success) {
        Modal.warning({
            title: 'Geolocalização',
            content: message,
        })
        return false
    }
    return true
}

function goLogin() {
    if (!requireGeolocation('Você precisa habilitar a geolocalização do seu navegador e estar na área de interesse.')) {
        return
    }
    window.location.href = '/login'
}

function goViability() {
    if (!requireGeolocation('Você precisa habilitar a geolocalização do seu navegador e estar na área de interesse.')) {
        return
    }
    window.location.href = '/viabilidade'
}

function ensureUserLocationLayer(map) {
    if (userLocationLayer) {
        return userLocationLayer
    }
    const source = new VectorSource()
    userLocationLayer = new VectorLayer({
        source,
        zIndex: 999,
        properties: { name: 'user-location' },
    })
    map.addLayer(userLocationLayer)
    return userLocationLayer
}

function updateUserLocation(map, lon, lat) {
    const layer = ensureUserLocationLayer(map)
    const source = layer.getSource()
    let feature = source.getFeatures()[0]
    const coord = fromLonLat([lon, lat])
    if (!feature) {
        feature = new Feature({ geometry: new Point(coord) })
        feature.setStyle(
            new Style({
                image: new CircleStyle({
                    radius: 8,
                    fill: new Fill({ color: '#1E90FF' }),
                    stroke: new Stroke({ color: '#ffffff', width: 2 }),
                }),
            })
        )
        source.addFeature(feature)
    } else {
        feature.getGeometry().setCoordinates(coord)
    }
}

function clearUserLocationLayer(map) {
    if (userLocationLayer) {
        map.removeLayer(userLocationLayer)
        userLocationLayer = null
    }
}

function startGeolocationWatch(map) {
    if (!('geolocation' in navigator)) {
        return
    }

    if (watchId != null && navigator.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(watchId)
        watchId = null
    }

    const onSuccess = (position) => {
        locationGranted.value = true
        updateUserLocation(map, position.coords.longitude, position.coords.latitude)
    }

    const onError = () => {
        locationGranted.value = false
        clearUserLocationLayer(map)
    }

    watchId = navigator.geolocation.watchPosition(onSuccess, onError, GEO_OPTIONS)
}

async function initWelcomeGeolocation(map) {
    if (!('geolocation' in navigator)) {
        Modal.warning({
            title: 'Geolocalização',
            content: 'Seu navegador não suporta geolocalização. Use um navegador atualizado para ver sua posição no mapa.',
        })
        return
    }

    try {
        const position = await getDeviceLocation()
        locationGranted.value = true
        updateUserLocation(map, position.longitude, position.latitude)
        startGeolocationWatch(map)

        if (window.checkLocation) {
            areaOfInterest.value = await checkAreaOfInterest()
            if (!areaOfInterest.value.success) {
                Modal.warning({
                    title: 'Fora da área do município',
                    content: areaOfInterest.value.message || 'Você está fora da área do município. Login e Viabilidade exigem estar na área de interesse.',
                })
            }
        }
    } catch {
        locationGranted.value = false
        clearUserLocationLayer(map)
        Modal.warning({
            title: 'Permissão de localização',
            content: 'Não foi possível obter sua localização. Permita o acesso nas configurações do navegador e recarregue a página.',
        })
    }
}

onMounted(async () => {
    const layerDefs = await fetchWelcomeMapConfig()
    const clientLayers = buildWelcomeMapLayers(layerDefs)

    const baseLayerList = baseLayers().getLayers().getArray()
    baseLayerList.forEach((layer) => layer.setVisible(false))
    const googleSatelliteRoads = baseLayerList.find((layer) => layer.get('title') === 'Google Satellite & Roads')
    if (googleSatelliteRoads) {
        googleSatelliteRoads.setVisible(true)
    }

    const map = new Map({
        target: 'welcome-map',
        layers: [baseLayers('g_sat'), ...clientLayers],
        view: new View(getMapDefaultCenterView()),
        interactions: defaultInteractions({
            doubleClickZoom: false,
            shiftDragZoom: false,
            keyboard: false,
        }),
        controls: defaultControlsOL({ rotate: false, zoom: false }).extend([
            scaleLine,
            fullScreenBtn,
            layerSwitcher,
        ]),
    })

    if (layerSwitcherTarget.value) {
        layerSwitcher.setTarget(layerSwitcherTarget.value)
    }

    mapRef.value = map
    await initWelcomeGeolocation(map)
})

onUnmounted(() => {
    if (watchId != null && navigator.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(watchId)
    }
    mapRef.value?.setTarget(null)
    mapRef.value = null
})
</script>

<style scoped>
.welcome-map-page {
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
}

#welcome-map.map {
    width: 100%;
    height: 100%;
}

.map-icons {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1111;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.map-icons a {
    cursor: pointer;
}

.map-icons a i {
    background-color: rgba(0, 60, 136, 0.5);
    color: white;
    font-size: 20px;
    padding: 10px;
    border: 2px solid white;
    border-radius: 10px;
    transition: transform 0.2s, color 0.2s, border-color 0.2s;
}

.map-icons a:hover i {
    color: lightblue;
    border-color: lightblue;
    transform: scale(1.1);
}

.map-layer-switch {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    max-height: calc(100vh - 20px);
    overflow: auto;
}

.layerSwitcher {
    min-width: 220px;
}
</style>
